/**
 * React Starter Kit (https://www.reactstarterkit.com/)
 *
 * Copyright © 2014-present Kriasoft, LLC. All rights reserved.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE.txt file in the root directory of this source tree.
 */

import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { interpret } from 'xstate';
import StyleContext from 'isomorphic-style-loader/StyleContext';
import { Provider as ReduxProvider } from 'react-redux';
import ApplicationContext from './ApplicationContext';
import seekMachine from './Seek/seekMachine';
import fmdMachine from './FMDQueryComponent/fmdMachine';
import { DEBUG_XSTATE_FMD, DEBUG_XSTATE_SEEK_PAGE } from '../settings';
import { isUsingDevToolsFromContext } from '../core/util';

/**
 * The top-level React component setting context (global) variables
 * that can be accessed from all the child components.
 *
 * https://facebook.github.io/react/docs/context.html
 *
 * Usage example:
 *
 *   const context = {
 *     history: createBrowserHistory(),
 *     store: createStore(),
 *   };
 *
 *   ReactDOM.render(
 *     <App context={context} insertCss={() => {}}>
 *       <Layout>
 *         <LandingPage />
 *       </Layout>
 *     </App>,
 *     container,
 *   );
 */

export default function App({ context, insertCss, children }) {
  // NOTE: If you need to add or modify header, footer etc. of the app,
  // please do that inside the Layout component.

  // eslint-disable-next-line react/prop-types
  const { store } = context;

  // This also lifts machine to global context, much simpler than SEEK example
  const [fmdService] = useState(
    interpret(fmdMachine, {
      devTools: isUsingDevToolsFromContext(context, DEBUG_XSTATE_FMD),
    }),
  );

  // NOTE: Here context from an XState machine is lifted to the global context.
  //  In the case of `seekService` we are lifting the seekMachine so that the machine
  //  is instantiated when the front page loads. Upon starting the machine immediately
  //  fetches the ModSEEK datasets for every organism (managed by `modSEEKDatasetManager`)
  //  so there is little to no delay when the user first visits the SEEK tool.
  const [seekService] = useState(
    interpret(seekMachine, {
      devTools: isUsingDevToolsFromContext(context, DEBUG_XSTATE_SEEK_PAGE),
    }),
  );

  useEffect(() => {
    fmdService.start();
    seekService.start();
  }, [fmdService, seekService]);

  return (
    <ReduxProvider store={store}>
      <StyleContext.Provider value={{ insertCss }}>
        <ApplicationContext.Provider
          value={{
            ...context,
            xstate: { fmdService, seekService },
          }}
        >
          {React.Children.only(children)}
        </ApplicationContext.Provider>
      </StyleContext.Provider>
    </ReduxProvider>
  );
}

App.propTypes = {
  // Enables critical path CSS rendering
  // https://github.com/kriasoft/isomorphic-style-loader
  insertCss: PropTypes.func.isRequired,
  context: PropTypes.shape({
    // Universal HTTP client
    fetch: PropTypes.func.isRequired,
    pathname: PropTypes.string.isRequired,
    query: PropTypes.object, // eslint-disable-line react/forbid-prop-types
    // Integrate Redux
    // http://redux.js.org/docs/basics/UsageWithReact.html
    ...ReduxProvider.childContextTypes,
  }).isRequired,
  children: PropTypes.element.isRequired,
};
